import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './InstructorDashboard.css';
import './ReportDashboard.css';
import Sidebar from './Sidebar';
import Header from './Header';
import HomePage from './HomePage';
import AdminUsers from './AdminUsers';
import InstructorCourses from './InstructorCourses';
import InstructorStudents from './InstructorStudents';
import ProfilModal from './ProfilModal';
import AdminExamSchedule from './AdminExamSchedule';
import { RectorSinavRaporu, AkademisyenSinavRaporu, ExamStudentsPage, CourseStatsPage } from './RectorDashboard';

function AdminDashboard({ user, onLogout, onImpersonate }) {
  const location = useLocation();
  const navigate = useNavigate();

  // URL'den sayfa bilgisini al (ör: /admin/users -> 'users')
  const getPageFromUrl = (pathname) => {
    if (pathname.startsWith('/admin/')) {
      const page = pathname.replace('/admin/', '');
      if (page === '' || page === 'home') return 'home';
      return page;
    }
    return 'home';
  };

  // localStorage'dan activePage'i yükle, yoksa URL'den al, yoksa 'home' kullan
  const [activePage, setActivePage] = useState(() => {
    const urlPage = getPageFromUrl(location.pathname);
    if (urlPage && urlPage !== 'home') return urlPage;
    const savedPage = localStorage.getItem('adminActivePage');
    return savedPage || 'home';
  });
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth > 768);
  const lastPathnameRef = useRef(location.pathname);
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);

  // URL değiştiğinde state'i güncelle (tarayıcı geri/ileri butonu için)
  useEffect(() => {
    // Sadece URL gerçekten değiştiyse (tarayıcı butonu ile) state'i güncelle
    if (location.pathname !== lastPathnameRef.current) {
      lastPathnameRef.current = location.pathname;
      const urlPage = getPageFromUrl(location.pathname);
      if (urlPage !== activePage) {
        setActivePage(urlPage);
        localStorage.setItem('adminActivePage', urlPage);
      }
    }
  }, [location.pathname, activePage]);

  // Mobil kontrolü
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) setIsSidebarOpen(true);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // activePage manuel değiştiğinde (menü tıklaması) URL'i güncelle
  const handlePageChange = (page) => {
    setActivePage(page);
    setSelectedExam(null);
    setSelectedCourse(null);
    const pagePath = page === 'home' ? '/admin/home' : `/admin/${page}`;
    lastPathnameRef.current = pagePath; // Ref'i güncelle ki useEffect tetiklenmesin
    navigate(pagePath); // replace: false - history'ye ekle (geri butonu için)
    localStorage.setItem('adminActivePage', page);
    if (isMobile) setIsSidebarOpen(false);
  };

  const renderPageContent = () => {
    switch (activePage) {
      case 'home':
        return <HomePage userType="admin" onPageChange={handlePageChange} />;

      case 'users':
        return <AdminUsers onImpersonate={onImpersonate} />;

      case 'exam-schedule':
        return <AdminExamSchedule />;

      case 'students':
        return <InstructorStudents instructorId={user?.id} isAdmin={true} onImpersonate={onImpersonate} />;

      case 'courses':
        return <InstructorCourses instructorId={user?.id} />;

      case 'universite-raporu':
        return <RectorSinavRaporu onCourseClick={setSelectedCourse} />;

      case 'akademisyen-raporu':
        return <AkademisyenSinavRaporu onCourseClick={setSelectedCourse} />;

      case 'profile':
        return (
          <div className="dashboard-section">
            <h2>Bilgilerim</h2>
            <div className="info-card">
              <div className="info-item">
                <span className="info-label">Admin ID:</span>
                <span className="info-value">{user?.id || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Kullanıcı Adı:</span>
                <span className="info-value">{user?.username || 'admin'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Ad Soyad:</span>
                <span className="info-value">{user?.name || 'Admin'}</span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="instructor-dashboard">
      <Header
        user={user}
        onLogout={onLogout}
        onProfileClick={() => setIsProfileModalOpen(true)}
        onHomeClick={() => handlePageChange('home')}
        onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        title="KOSTÜ Sınav Modülü"
      />

      <Sidebar
        userType="admin"
        activePage={activePage}
        onPageChange={handlePageChange}
        user={user}
        isOpen={isMobile ? isSidebarOpen : true}
        onClose={isMobile ? () => setIsSidebarOpen(false) : undefined}
      />
      
      <div className="dashboard-main">
        <div className={`dashboard-content ${activePage === 'home' ? 'full-height' : ''}`}>
          {selectedExam ? (
            <div className="dashboard-container">
              <ExamStudentsPage exam={selectedExam} onBack={() => setSelectedExam(null)} />
            </div>
          ) : selectedCourse ? (
            <div className="dashboard-container">
              <CourseStatsPage course={selectedCourse} onBack={() => setSelectedCourse(null)} onExamClick={setSelectedExam} />
            </div>
          ) : activePage === 'home' || activePage === 'universite-raporu' || activePage === 'akademisyen-raporu' ? (
            renderPageContent()
          ) : (
            <div className="dashboard-container">
              {renderPageContent()}
            </div>
          )}
        </div>
      </div>

      <ProfilModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        user={user}
        userType="admin"
      />
    </div>
  );
}

export default AdminDashboard;

